import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, Plus, Edit2, Trash2, DollarSign, 
  Tag, Check, X, Zap, Package, ChevronUp, ChevronDown, AlertTriangle
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import BottomNav from "@/components/BottomNav";
import { MeshPhotosSettings } from "@/components/MeshPhotosSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getSessionToken = () => localStorage.getItem('session_token');

function ItemFlags({ item }) {
  const flags = [
    item.item_type === "discount" && [`Discount ${item.discount_type === "percent" ? `${item.discount_value}%` : `$${item.discount_value}`}`, "bg-green-100 text-green-800"],
    item.is_active === false && ["Hidden from customers", "bg-amber-100 text-amber-800"],
    item.archived && ["Archived", "bg-zinc-200 text-zinc-700"],
  ].filter(Boolean);
  return flags.map(([label, cls]) => (
    <span key={label} className={`text-xs px-2 py-0.5 rounded-full ${cls}`} data-testid={`item-flag-${item.item_id}`}>{label}</span>
  ));
}


export default function ItemsPricing() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    material_cost: "",
    is_taxed: false,
    show_on_quick_add: true,
    category: "",
    description: "",
    funnel_slot: "",
    is_active: true,
    archived: false,
  });

  // Canonical Instant Quote slots — must match backend FUNNEL_SLOTS list
  // exactly. Assigning a slot guarantees the Instant Quote engine reads
  // this item's price regardless of the display name.
  const FUNNEL_SLOT_OPTIONS = [
    { value: "", label: "— Not used in Instant Quote —" },
    { value: "window_rescreen", label: "Window Rescreen" },
    { value: "window_new", label: "New Window Screen" },
    { value: "slider_rescreen", label: "Sliding Door Rescreen" },
    { value: "slider_new", label: "New Sliding Door Screen" },
    { value: "petscreen_upgrade", label: "PetScreen Upgrade (Door)" },
    { value: "solar_door", label: "Solar Screen Upgrade (Door)" },
    { value: "door_disposal", label: "Old Door Disposal" },
  ];

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const token = getSessionToken();
      const response = await axios.get(`${API}/pricing-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(response.data);
    } catch (error) {
      toast.error("Failed to load pricing items");
    } finally {
      setLoading(false);
    }
  };

  // Move a quick-add item up or down. Rebuilds the item_ids array for the
  // quick-add subset only and PUTs to /pricing-items/reorder. Non-quick-add
  // items keep their existing `order` untouched.
  const moveQuickAddItem = async (itemId, direction) => {
    const qa = items
      .filter((it) => it.show_on_quick_add)
      .slice()
      .sort((a, b) => {
        // Match backend sort: order asc (None last), then name.
        const ao = a.order ?? Number.POSITIVE_INFINITY;
        const bo = b.order ?? Number.POSITIVE_INFINITY;
        if (ao !== bo) return ao - bo;
        return (a.name || "").localeCompare(b.name || "");
      });
    const idx = qa.findIndex((it) => it.item_id === itemId);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= qa.length) return;
    const reordered = qa.slice();
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      const token = getSessionToken();
      await axios.put(
        `${API}/pricing-items/reorder`,
        { item_ids: reordered.map((it) => it.item_id) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reorder");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.price) {
      toast.error("Name and price are required");
      return;
    }

    try {
      const token = getSessionToken();
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        material_cost: parseFloat(formData.material_cost) || 0
      };

      if (editingItem) {
        await axios.put(`${API}/pricing-items/${editingItem.item_id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Item updated");
      } else {
        await axios.post(`${API}/pricing-items`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Item created");
      }

      setDialogOpen(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      const token = getSessionToken();
      await axios.delete(`${API}/pricing-items/${itemToDelete.item_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Item deleted");
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchItems();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      price: item.price.toString(),
      material_cost: (item.material_cost || 0).toString(),
      is_taxed: item.is_taxed,
      show_on_quick_add: item.show_on_quick_add,
      category: item.category || "",
      description: item.description || "",
      funnel_slot: item.funnel_slot || "",
      is_active: item.is_active !== false,
      archived: !!item.archived,
      ...(item.item_type === "discount" ? { discount_value: item.discount_value ?? 10 } : {}),
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    resetForm();
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      price: "",
      material_cost: "",
      is_taxed: false,
      show_on_quick_add: true,
      category: "",
      description: "",
      funnel_slot: "",
      is_active: true,
      archived: false,
    });
    setEditingItem(null);
  };

  const confirmDelete = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  // Match the backend's sort so the up/down arrows behave predictably
  // (order asc with nulls last, then name).
  const byQuickAddOrder = (a, b) => {
    const ao = a.order ?? Number.POSITIVE_INFINITY;
    const bo = b.order ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return (a.name || "").localeCompare(b.name || "");
  };
  const quickAddItems = items.filter(item => item.show_on_quick_add).sort(byQuickAddOrder);
  const otherItems = items.filter(item => !item.show_on_quick_add);

  // Detect duplicate funnel-slot assignments so the admin can spot them
  // (usually left over from a previous auto-provision + rename combo).
  const slotCounts = {};
  for (const it of items) {
    const s = (it.funnel_slot || "").trim().toLowerCase();
    if (!s) continue;
    slotCounts[s] = (slotCounts[s] || 0) + 1;
  }
  const duplicateSlots = Object.entries(slotCounts)
    .filter(([, count]) => count > 1)
    .map(([slot]) => slot);
  const slotLabelById = FUNNEL_SLOT_OPTIONS.reduce((acc, o) => {
    if (o.value) acc[o.value] = o.label;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-zinc-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Items & Pricing</h1>
              <p className="text-sm text-zinc-500">Manage your products and services</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        <MeshPhotosSettings />
        {/* Duplicate funnel-slot warning — multiple items pointing at the
            same Instant Quote slot means the funnel picks the FIRST one
            it finds, and revenue reporting can double-count. */}
        {duplicateSlots.length > 0 && (
          <div
            className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4"
            data-testid="duplicate-slot-warning"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-bold text-amber-900">
                  Two or more items share the same Instant Quote slot
                </p>
                <p className="text-amber-800 mt-1">
                  Only one item per slot should be pinned to the funnel. Delete or
                  re-assign the duplicates below:
                </p>
                <ul className="mt-2 space-y-1 text-amber-800">
                  {duplicateSlots.map((slot) => (
                    <li key={slot} className="text-xs">
                      <strong>{slotLabelById[slot] || slot}</strong> —{" "}
                      {items
                        .filter((it) => (it.funnel_slot || "").toLowerCase() === slot)
                        .map((it) => `"${it.name}" ($${it.price})`)
                        .join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Add New Button */}
        <button
          onClick={openCreateDialog}
          className="w-full py-3 bg-[#207024] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#1a5c1d]"
        >
          <Plus className="w-5 h-5" />
          Add New Item
        </button>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border">
            <Package className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500">No pricing items yet</p>
            <p className="text-sm text-zinc-400">Add your first product or service</p>
          </div>
        ) : (
          <>
            {/* Quick Add Items */}
            {quickAddItems.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <h2 className="font-semibold text-zinc-700">Quick Add Items</h2>
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    {quickAddItems.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {quickAddItems.map((item, idx) => (
                    <div
                      key={item.item_id}
                      className="bg-white border rounded-xl p-4 flex items-center justify-between"
                      data-testid={`quick-add-item-${item.item_id}`}
                    >
                      <div className="flex flex-col mr-2">
                        <button
                          type="button"
                          onClick={() => moveQuickAddItem(item.item_id, "up")}
                          disabled={idx === 0}
                          data-testid={`move-up-${item.item_id}`}
                          className="p-1 hover:bg-zinc-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Move up"
                        >
                          <ChevronUp className="w-4 h-4 text-zinc-600" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveQuickAddItem(item.item_id, "down")}
                          disabled={idx === quickAddItems.length - 1}
                          data-testid={`move-down-${item.item_id}`}
                          className="p-1 hover:bg-zinc-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Move down"
                        >
                          <ChevronDown className="w-4 h-4 text-zinc-600" />
                        </button>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{item.name}</span>
                          <ItemFlags item={item} />
                          {item.is_taxed && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Taxed
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-2">
                          <div className="text-[#207024] font-bold">{item.item_type === "discount" ? `${item.discount_value}% off job` : `$${item.price.toFixed(2)}`}</div>
                          {item.material_cost > 0 && (
                            <div className="text-xs text-zinc-500">mat ${item.material_cost.toFixed(2)}</div>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-xs text-zinc-500 mt-1">{item.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditDialog(item)}
                          data-testid={`edit-item-${item.item_id}`}
                          className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => confirmDelete(item)}
                          className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other Items */}
            {otherItems.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-zinc-500" />
                  <h2 className="font-semibold text-zinc-700">Other Items</h2>
                  <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">
                    {otherItems.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {otherItems.map((item) => (
                    <div
                      key={item.item_id}
                      className="bg-white border rounded-xl p-4 flex items-center justify-between opacity-75"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{item.name}</span>
                          <ItemFlags item={item} />
                          {item.is_taxed && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                              Taxed
                            </span>
                          )}
                        </div>
                        <div className="flex items-baseline gap-2">
                          <div className="text-[#207024] font-bold">{item.item_type === "discount" ? `${item.discount_value}% off job` : `$${item.price.toFixed(2)}`}</div>
                          {item.material_cost > 0 && (
                            <div className="text-xs text-zinc-500">mat ${item.material_cost.toFixed(2)}</div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditDialog(item)}
                          data-testid={`edit-item-${item.item_id}`}
                          className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => confirmDelete(item)}
                          className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/* iOS-safe scroll: use `dvh` (dynamic viewport height) — iOS
            Safari miscomputes `vh` against the largest viewport (no
            address bar), which made the bottom of the form spill below
            the screen even with max-h-[90vh]. Also restructure as a flex
            column so the form body scrolls while the action buttons
            stick to the dialog's bottom — guarantees Cancel/Save are
            ALWAYS reachable no matter how tall the form gets. */}
        <DialogContent className="max-w-md max-h-[85dvh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>{editingItem ? "Edit Item" : "Add New Item"}</DialogTitle>
            <DialogDescription className="sr-only">
              {editingItem ? "Edit pricing item" : "Create a new pricing item"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-6 pt-2 pb-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Item Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Window Screen Rescreen"
                className="w-full h-11 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Price *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="0.00"
                  className="w-full h-11 rounded-lg border-2 border-zinc-200 pl-9 pr-3 focus:border-[#207024] focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Material Cost (Optional)
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.material_cost}
                  onChange={(e) => setFormData({ ...formData, material_cost: e.target.value })}
                  placeholder="0.00"
                  data-testid="material-cost-input"
                  className="w-full h-11 rounded-lg border-2 border-zinc-200 pl-9 pr-3 focus:border-[#207024] focus:outline-none"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">Per-unit cost of materials. Used to calculate margin.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Show in Instant Quote as
              </label>
              <select
                value={formData.funnel_slot}
                onChange={(e) => setFormData({ ...formData, funnel_slot: e.target.value })}
                className="w-full h-11 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024] focus:outline-none"
                data-testid="pricing-funnel-slot"
              >
                {FUNNEL_SLOT_OPTIONS.map((opt) => (
                  <option key={opt.value || "none"} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <div className="text-[11px] text-zinc-500 mt-1 leading-snug space-y-1">
                <p>
                  <strong>Pick which service in the funnel this item is for.</strong>{" "}
                  The Instant Quote uses your dropdown choice — not the item name — so
                  a &quot;Window Rescreen&quot; and a &quot;Sliding Door Rescreen&quot; will never be
                  confused even if the names look similar.
                </p>
                <p>
                  Every funnel slot should point to exactly ONE item. Leave &quot;Not
                  used&quot; for portal-only items (add-ons, discounts, etc.) that
                  shouldn&apos;t appear in the customer&apos;s quote.
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Category (Optional)
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full h-11 rounded-lg border-2 border-zinc-200 px-3 focus:border-[#207024] focus:outline-none"
              >
                <option value="">-- Select Category --</option>
                <option value="rescreen">Rescreen</option>
                <option value="new_build">New Build</option>
                <option value="repair">Repair</option>
                <option value="other">Other</option>
                <option value="door">Door</option>
                <option value="discount">Discount</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-1">
                Description (Optional)
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the item"
                rows={2}
                className="w-full rounded-lg border-2 border-zinc-200 px-3 py-2 focus:border-[#207024] focus:outline-none resize-none"
              />
            </div>

            <div className="space-y-3 pt-2">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                <input
                  type="checkbox"
                  checked={formData.is_taxed}
                  onChange={(e) => setFormData({ ...formData, is_taxed: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                />
                <div>
                  <div className="font-medium">Taxable</div>
                  <div className="text-sm text-zinc-500">Apply sales tax to this item</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                <input
                  type="checkbox"
                  checked={formData.show_on_quick_add}
                  onChange={(e) => setFormData({ ...formData, show_on_quick_add: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]"
                />
                <div>
                  <div className="font-medium flex items-center gap-2">
                    <Zap className="w-4 h-4 text-amber-500" />
                    Show on Quick Add
                  </div>
                  <div className="text-sm text-zinc-500">Display in quick add menu when creating estimates</div>
                </div>
              </label>

              {formData.discount_value !== undefined && (
                <label className="flex items-center gap-3 p-3 border rounded-lg">
                  <span className="font-medium flex-1">Discount % off job total</span>
                  <input type="number" min="0" max="100" value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                    className="w-20 h-9 rounded border px-2 text-sm" data-testid="item-discount-value" />
                </label>
              )}

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                <input type="checkbox" checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]" data-testid="item-active-checkbox" />
                <div>
                  <div className="font-medium">Show to customers</div>
                  <div className="text-sm text-zinc-500">Off = hidden from the quote tools and portal; techs can still add it</div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
                <input type="checkbox" checked={formData.archived}
                  onChange={(e) => setFormData({ ...formData, archived: e.target.checked })}
                  className="w-5 h-5 rounded border-zinc-300 text-[#207024] focus:ring-[#207024]" data-testid="item-archived-checkbox" />
                <div>
                  <div className="font-medium">Archived</div>
                  <div className="text-sm text-zinc-500">Hidden everywhere; past invoices and reports are unaffected</div>
                </div>
              </label>
            </div>

            </div>

            {/* Sticky footer — always reachable on mobile regardless of
                how much form content above. */}
            <div className="flex gap-3 px-6 py-3 border-t bg-white">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="flex-1 py-3 border-2 border-zinc-200 rounded-lg font-semibold hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="item-save-btn"
                className="flex-1 py-3 bg-[#207024] text-white rounded-lg font-semibold hover:bg-[#1a5c1d]"
              >
                {editingItem ? "Save Changes" : "Add Item"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{itemToDelete?.name}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setItemToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
}
